import { isWeb, VisuallyHidden, YStack } from "tamagui";
import { ChevronLeft as ChevronLeftIcon } from "@tamagui/lucide-icons";
import { Button, CenterContent, HeaderContainer, HeaderSide, ProgressBar } from "@medbillai/ui";
import { GrantedWordmark } from "../local-images";
const WORDMARK_WIDTH = 101;
type OnboardingProgressHeaderProps = {
  /**
   * Progress value between 0 and 100. When omitted, the progress bar is
   * hidden; the rest of the header (wordmark or back button) still renders.
   */
  progress?: number;
  /**
   * Native only — when provided, shows a back button on the left and wires it
   * to this handler. Web uses the browser's back button for navigation.
   */
  onBackPress?: () => void;
  /** Accessible title. Not rendered visibly; used for screen-reader context. */
  title?: string;
  /** Web only — render the Granted wordmark in the left slot. @default true */
  showLogo?: boolean;
};

/**
 * Onboarding-flow header. Uses styles from `NavHeader`'s progressBar layout but
 * doesnt reuse the component so the progress bar can be optional without touching the shared `NavHeader`.
 *
 * On web: Granted wordmark on the left, optional progress bar centered, a
 * symmetric spacer on the right to keep the progress bar visually centered.
 * Onboarding on web never shows a back button.
 *
 * On native: standard back-button layout (like `NavHeader` progressBar), with
 * the progress bar optional. No close button — onboarding flows don't expose
 * a dismiss affordance.
 */
export function OnboardingProgressHeader({
  progress,
  onBackPress,
  title = "Onboarding",
  showLogo = true
}: OnboardingProgressHeaderProps) {
  const progressCenter = progress !== undefined ? <ProgressBar value={progress} width="$si10" /> : null;
  if (isWeb) {
    return <HeaderContainer>
        <VisuallyHidden>{title}</VisuallyHidden>
        {showLogo ? <YStack flexShrink={0}>
            <GrantedWordmark />
          </YStack> : <YStack flexShrink={0} width={WORDMARK_WIDTH} />}
        <CenterContent>{progressCenter}</CenterContent>
        <YStack flexShrink={0} width={WORDMARK_WIDTH} />
      </HeaderContainer>;
  }
  return <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-component="OnboardingProgressHeader" data-sentry-source-file="OnboardingProgressHeader.tsx">
      <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="OnboardingProgressHeader.tsx">{title}</VisuallyHidden>
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="OnboardingProgressHeader.tsx">
        {onBackPress ? <Button variant="tertiary" size="large" icon={ChevronLeftIcon} aria-label="Go back" onPress={onBackPress} /> : null}
      </HeaderSide>
      <CenterContent data-sentry-element="CenterContent" data-sentry-source-file="OnboardingProgressHeader.tsx">{progressCenter}</CenterContent>
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="OnboardingProgressHeader.tsx" />
    </HeaderContainer>;
}